import { useEffect, useState, useRef, useContext } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { fetchSettings } from "../AppData";
import { useToast } from "../context/ToastProvider";
import { fetchSearchSuggestions } from "../api/GlobalApi";
import { AuthContext } from "../context/AuthContext";

import { useTranslation } from '../hooks/useTranslation';

// Load cached settings immediately (before component renders)
const getCachedSettings = () => {
  try {
    const cached = localStorage.getItem('app_settings');
    return cached ? JSON.parse(cached) : {};
  } catch (error) {
    console.error('Error loading cached settings:', error);
    return {};
  }
};

function Header() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user, logout } = useContext(AuthContext); // ✅ use global context
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [settings, setSettings] = useState(getCachedSettings()); // Initialize with cache

  // Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);
  const { t, lang, switchLanguage } = useTranslation();

  useEffect(() => {
    // Fetch fresh settings from API and update cache
    fetchSettings().then((data) => {
      setSettings(data);
      // Cache settings in localStorage for instant future loads
      localStorage.setItem('app_settings', JSON.stringify(data));
    }).catch((error) => {
      console.error('Error fetching settings:', error);
      // If API fails and we have cache, keep using it
    });
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch suggestions as user types
  useEffect(() => {
    const delayTimer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        loadSuggestions();
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(delayTimer);
  }, [searchQuery]);

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const data = await fetchSearchSuggestions(searchQuery);
      setSuggestions(data);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (slug) => {
    navigate(`/app/details/${slug}`);
    setSearchQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  const handleLogout = () => {
    logout(); // ✅ Use global logout from AuthContext
    setIsDropdownOpen(false);
    addToast("You have been logged out successfully", "error", 3000);
    navigate("/");
  };

  const getUserDisplay = () => {
    if (!user) return "U"; // Default if user not logged in

    if (user.avatar) {
      // If avatar is available
      return (
        <img
          src={user.avatar}
          alt={user.name}
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
      );
    }

    // If avatar not available, show user name
    return (
      <span
        style={{
          fontWeight: "500",
          fontSize: "14px",
          color: "#333",
          textTransform: "capitalize",
        }}
      >
        {user.name}
      </span>
    );
  };


  // Get user initial for profile button
  const getUserInitial = () => {
    if (user && user.name) {
      return user.name.charAt(0).toUpperCase();
    }
    return "U";
  };

  // Render logo or site name based on availability
  const renderLogo = () => {
    // Show placeholder immediately while settings load
    if (!settings.site_logo && !settings.site_name) {
      return (
        <>
          <div className="logo-icon"></div>
          <span style={{ fontSize: "22px", fontWeight: 400 }}>
            AppStore
          </span>
        </>
      );
    }

    if (settings.site_logo) {
      return (
        <img
          src={settings.site_logo}
          alt={settings.site_name || "Logo"}
          style={{
            height: "32px",
            objectFit: "contain",
            maxWidth: "180px"
          }}
          loading="eager" // Load logo immediately
          onError={(e) => {
            // Fallback if logo fails to load
            e.target.style.display = 'none';
            e.target.parentElement.innerHTML = `<div class="logo-icon"></div><span style="font-size: 22px; font-weight: 400">${settings.site_name || "AppStore"}</span>`;
          }}
        />
      );
    }

    return (
      <>
        <div className="logo-icon"></div>
        <span style={{ fontSize: "22px", fontWeight: 400 }}>
          {settings.site_name || "AppStore"}
        </span>
      </>
    );
  };

  return (
    <>
      <header className="header">
        <div className="header-content">
          <button className="menu-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
            </svg>
          </button>

          <Link to="/" className="logo">
            {renderLogo()}
          </Link>

          <div
            className="search-container"
            ref={searchRef}
            style={{ position: "relative" }}
          >
            <form onSubmit={handleSearch}>
              <svg className="search-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
              </svg>
              <input
                type="text"
                className="search-box"
                placeholder="Search for apps & games"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() =>
                  searchQuery.length >= 2 && setShowSuggestions(true)
                }
              />
            </form>

            {/* Search Suggestions Dropdown */}
            {showSuggestions && (
              <div className="search-suggestions">
                {loading ? (
                  <div className="suggestion-loading">{t('Searching')}...</div>
                ) : suggestions.length > 0 ? (
                  <>
                    {suggestions.map((app) => (
                      <div
                        key={app.id}
                        className="suggestion-item"
                        onClick={() => handleSuggestionClick(app.slug)}
                      >
                        <img
                          src={app.icon_url}
                          alt={app.name}
                          className="suggestion-icon"
                        />
                        <div className="suggestion-info">
                          <div className="suggestion-name">{app.name}</div>
                          <div className="suggestion-category">
                            {app.category}
                          </div>
                        </div>
                        {app.rating && (
                          <div className="suggestion-rating">
                            ★ {parseFloat(app.rating).toFixed(1)}
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="suggestion-view-all" onClick={handleSearch}>
                      {t('View all results for')} "{searchQuery}"
                    </div>
                  </>
                ) : searchQuery.length >= 2 ? (
                  <div className="suggestion-empty">{t('No results found')}</div>
                ) : null}
              </div>
            )}
          </div>

          <div className="profile-section" style={{ position: "relative" }}>
            <button className="help-btn" onClick={() => navigate("/help")} title="Help">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
              </svg>
            </button>

            {user && (
              <button style={{ textDecoration: "none" }} className="settings-btn" title="Settings" onClick={() => navigate("/profile")}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58
                  c0.18-0.14,0.23-0.41,0.12-0.61l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96
                  c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41h-3.84
                  c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33
                  c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58
                  C4.84,11.36,4.82,11.69,4.82,12s0.02,0.64,0.07,0.94l-2.03,1.58c-0.18,0.14-0.23,0.41-0.12,0.61
                  l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54
                  c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54
                  c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32
                  c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6
                  s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z" />
                </svg>
              </button>
            )}

            <button className="profile-btn" onClick={toggleDropdown}>
              {getUserDisplay()}
            </button>

            {isDropdownOpen && (
              <div
                className="dropdown-menu"
                style={{
                  position: "absolute",
                  top: "40px",
                  right: 0,
                  background: "#fff",
                  border: "1px solid #dadce0",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  minWidth: "150px",
                  zIndex: 1000,
                }}
              >
                {!user ? (
                  <>
                    <Link
                      to="/login"
                      className="dropdown-item"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {t('Login')}
                    </Link>
                    <Link
                      to="/register"
                      className="dropdown-item"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {t('Register')}
                    </Link>
                  </>
                ) : (
                  <>
                    <div
                      className="dropdown-item"
                      style={{
                        fontWeight: 500,
                        borderBottom: "1px solid #dadce0",
                      }}
                    >
                      {user?.name}
                    </div>
                    <Link
                      to="/profile"
                      className="dropdown-item"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {t('Profile')}
                    </Link>
                    <button onClick={handleLogout} className="dropdown-item">
                      {t('Logout')}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <nav className="nav-container">
        <div className="nav-content">
          <NavLink to="/" className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}>
            {t('Home')}
          </NavLink>
          <NavLink to="/apps" className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}>
            {t('Apps')}
          </NavLink>
          <NavLink to="/games" className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}>
            {t('Games')}
          </NavLink>
          <NavLink to="/blogs" className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}>
            {t('Blogs')}
          </NavLink>
        </div>
      </nav>
    </>
  );
}

export default Header;